import { observer } from "mobx-react-lite";
import { useFormikContext } from "formik";
import { ArrowLeftRight, BarChart3, ListFilter, RefreshCw } from "lucide-react";
import { createElement } from "react";
import { useTran } from "@/hooks";
import { FormField, SelectField } from "@/components/common";
import { Switch } from "@/components/ui/switch";
import { LOCATION_OPTIONS } from "@/lib/constants";
import { cn } from "@/lib/utils";

const TARGET_FREQUENCY_OPTIONS = [
  { value: "monthly", labelKey: "Monthly" },
  { value: "yearly", labelKey: "Yearly" },
];

const SYSTEM_SETTING_OPTIONS = [
  {
    key: "enableMonthlyYearlySwitch",
    titleKey: "Allow Monthly/Yearly Switching",
    descriptionKey:
      "Allow users to manually switch between Monthly and Yearly target views.",
    Icon: ArrowLeftRight,
  },
  {
    key: "enableViewDetailsSwitch",
    titleKey: "Auto Switch Target Data Overview",
    descriptionKey:
      'Automatically switch the "View Details" page based on the configured target frequency.',
    Icon: RefreshCw,
  },
  {
    key: "showCategoryDashboardWidgets",
    titleKey: "Show Category Dashboard Widgets",
    descriptionKey: "Display the following widgets on the dashboard:",
    details: [
      "ESG Disclosure Progress by Category",
      "Completed Targets by Category",
    ],
    Icon: BarChart3,
  },
  {
    key: "showOnlyMetricsWithTargetAndData",
    titleKey: "Show Only Metrics With Target And Data",
    descriptionKey:
      "On Target and Data Overview, show only metrics that have both a target and submitted data, instead of all metrics with either.",
    Icon: ListFilter,
  },
];

const renderSystemSettingIcon = (Icon) =>
  createElement(Icon, {
    className: "h-4 w-4",
    "aria-hidden": true,
  });

const CompanySetupStepOne = observer(
  ({
    showTargetFrequency = false,
    companyNameError,
    companyNameDescription,
    companyNameDescriptionClassName,
  }) => {
    const { t } = useTran();
    const {
      values,
      touched,
      errors,
      handleBlur,
      setFieldValue,
      setFieldTouched,
    } = useFormikContext();

    const getFieldError = (key) => (touched?.[key] ? errors?.[key] : undefined);

    const handleCompanyNameChange = (event) => {
      const nextValue = event?.target?.value || "";
      setFieldValue("companyName", nextValue, true);
      setFieldTouched("companyName", true, false);
    };

    const handleCountryChange = (event) => {
      const nextValue = event?.target?.value || "";
      setFieldValue("country", nextValue, true);
      setFieldTouched("country", true, false);
    };

    const handleTargetFrequencyChange = (value) => {
      setFieldValue("targetFrequency", value, true);
      setFieldTouched("targetFrequency", true, false);
    };

    const handleSystemSettingChange = (key, checked) => {
      setFieldValue(key, Boolean(checked), true);
      setFieldTouched(key, true, false);
    };

    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <FormField
            label={t("What is your company name?")}
            required
            name="companyName"
            value={values.companyName}
            onChange={handleCompanyNameChange}
            onBlur={handleBlur}
            placeholder="Acme Inc."
            error={companyNameError || getFieldError("companyName")}
          />
          {companyNameDescription ? (
            <p
              className={
                companyNameDescriptionClassName ||
                "text-xs text-muted-foreground"
              }
            >
              {companyNameDescription}
            </p>
          ) : null}
        </div>

        <SelectField
          id="country"
          label={t("Where is your company located?")}
          required
          placeholder={t("Select a country")}
          value={values.country}
          options={LOCATION_OPTIONS}
          onChange={handleCountryChange}
          error={getFieldError("country")}
        />

        {showTargetFrequency ? (
          <div className="space-y-3">
            <div className="space-y-2">
              <div>
                <p className="text-sm font-medium text-foreground">
                  {t("Target Setup Frequency")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("How targets will be recorded for this company.")}
                </p>
              </div>
              <div className="grid w-full grid-cols-2 gap-1 rounded-lg border border-emerald-200 bg-emerald-50/60 p-1">
                {TARGET_FREQUENCY_OPTIONS.map((option) => {
                  const isActive =
                    (values.targetFrequency || "yearly") === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleTargetFrequencyChange(option.value)}
                      className={cn(
                        "h-8 rounded-md text-xs font-semibold transition-all",
                        isActive
                          ? "bg-nxstart-green text-white shadow-sm"
                          : "text-emerald-700 hover:bg-white/70",
                      )}
                    >
                      {t(option.labelKey)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-3">
              <div className="mb-3">
                <p className="text-sm font-semibold text-foreground">
                  {t("System Settings")}{" "}
                  <span className="font-normal text-muted-foreground">
                    ({t("Optional")})
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("Configure additional preferences for your company.")}
                </p>
              </div>

              <div className="space-y-3">
                {SYSTEM_SETTING_OPTIONS.map(
                  ({ key, titleKey, descriptionKey, details, Icon }) => (
                    <div
                      key={key}
                      className="grid grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-3"
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded-md bg-emerald-100 text-emerald-700">
                        {renderSystemSettingIcon(Icon)}
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-foreground">
                          {t(titleKey)}
                        </p>
                        <p className="text-[11px] leading-4 text-muted-foreground">
                          {t(descriptionKey)}
                        </p>
                        {details?.length ? (
                          <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[11px] leading-4 text-muted-foreground">
                            {details.map((detail) => (
                              <li key={detail}>{t(detail)}</li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                      <Switch
                        checked={values[key] !== false}
                        onCheckedChange={(checked) =>
                          handleSystemSettingChange(key, checked)
                        }
                        className="data-[state=checked]:bg-nxstart-green"
                        aria-label={t(titleKey)}
                      />
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  },
);

export default CompanySetupStepOne;
